import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Loader2, Search, Send, X } from 'lucide-react'
import { fetchStaffDirectory } from '../../../services/staffDirectoryService'
import { sendPolicyInvite } from '../../../services/adminPolicyService'

function initials(name, email) {
  const source = (name || email || '?').trim()
  const parts = source.split(/\s+/).filter(Boolean)
  const letters = parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`
    : source.slice(0, 2)
  return letters.toUpperCase()
}

export default function AdminPolicySendInviteModal({ isOpen, onClose, policy = null, onSent }) {
  const [query, setQuery] = useState('')
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(null) // { id, name, email }
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    setQuery('')
    setSelected(null)
    setError('')
    setDone(false)
    setLoading(true)
    fetchStaffDirectory({ perPage: 100 })
      .then((result) => setStaff(result?.staff ?? []))
      .catch(() => {
        setStaff([])
        setError('Unable to load the staff directory.')
      })
      .finally(() => setLoading(false))
  }, [isOpen])

  // Re-search the directory when the admin types (server-side search).
  useEffect(() => {
    if (!isOpen) return
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setLoading(true)
      fetchStaffDirectory({ search: query.trim() || undefined, perPage: 100 })
        .then((result) => setStaff(result?.staff ?? []))
        .catch(() => setStaff([]))
        .finally(() => setLoading(false))
    }, query ? 300 : 0)
    return () => clearTimeout(debounceRef.current)
  }, [query, isOpen])

  const list = useMemo(() => staff.filter((m) => m && m.email), [staff])

  if (!isOpen) return null

  const pick = (member) => {
    setSelected({
      id: member.id,
      name: member.name || `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim(),
      email: member.email,
    })
    setError('')
  }

  const handleSend = async () => {
    if (!selected?.email) {
      setError('Select a staff member first.')
      return
    }
    setSending(true)
    setError('')
    try {
      await sendPolicyInvite(policy.id, {
        email: selected.email,
        recipient_name: selected.name || undefined,
      })
      setDone(true)
      onSent?.(selected)
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to send the invitation right now.')
    } finally {
      setSending(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 bg-gray-900/50" />
      <div className="relative z-10 my-8 flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">Send signing invitation</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {policy?.name ? <>Emailed for <span className="font-semibold text-gray-700">{policy.name}</span></> : 'Pick a staff member from the directory'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-10 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Check size={30} aria-hidden />
            </span>
            <h3 className="mt-4 text-base font-bold text-gray-900">Invitation sent</h3>
            <p className="mt-1 text-sm text-gray-600">
              The signing link was emailed to <span className="font-semibold">{selected?.name || selected?.email}</span>.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-gray-800"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4">
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search staff by name or email…"
                  className="w-full rounded-lg bg-gray-100 py-2.5 pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-rapid-orange/30"
                />
              </div>
            </div>

            <div className="mt-2 flex-1 overflow-y-auto px-3 py-2">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin" aria-hidden /> Loading staff…
                </div>
              ) : list.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-500">No staff match your search.</p>
              ) : (
                <ul className="space-y-1">
                  {list.map((member) => {
                    const isSelected = selected?.id === member.id
                    return (
                      <li key={member.id}>
                        <button
                          type="button"
                          onClick={() => pick(member)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                            isSelected ? 'bg-orange-50 ring-1 ring-rapid-orange/40' : 'hover:bg-gray-50'
                          }`}
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-rapid-orange">
                            {initials(member.name, member.email)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-gray-900">{member.name || member.email}</span>
                            <span className="block truncate text-xs text-gray-500">{member.email}</span>
                          </span>
                          {member.job_title ? (
                            <span className="hidden shrink-0 text-xs text-gray-400 sm:block">{member.job_title}</span>
                          ) : null}
                          {isSelected ? <Check size={16} className="shrink-0 text-rapid-orange" aria-hidden /> : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            <div className="border-t border-gray-100 px-6 py-4">
              {error ? (
                <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              ) : null}
              {selected ? (
                <p className="mb-3 text-xs text-gray-500">
                  Sending to <span className="font-semibold text-gray-800">{selected.name || selected.email}</span> · {selected.email}
                </p>
              ) : null}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || !selected}
                  className="inline-flex items-center gap-2 rounded-lg bg-rapid-orange px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:opacity-60"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Send size={15} aria-hidden />}
                  {sending ? 'Sending…' : 'Send invitation'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
